<script lang="ts" setup>
import { useInfiniteQuery } from '@tanstack/vue-query';
import { useRoute } from 'vue-router/composables';
import { computed, ref } from 'vue';
import { useStore } from '@/store';
import BcSpinner from '@/ui-kit/components/BcSpinner/BcSpinner.vue';
import CircularProgress from '@/components/CircularProgress.vue';
import InfiniteScrollTrigger from '@/components/InfiniteScroll/InfiniteScrollTrigger.vue';
import { getCampaignStatistics, StatisticsPage, CampaignsStatistics } from '@/api/campaigns';
import { usePanelStore } from '@/store/pinia/panel';
import BcModal from '@/ui-kit/components/BcModal/BcModal.vue';
import CampaignStatisticsCard from '@/components/CampaignStatisticsCard.vue';

// Interface for infinite query data
interface InfiniteStatisticsData {
  pages: StatisticsPage[];
  pageParams: (string | undefined)[];
}

const route = useRoute();
const store = useStore();
const panelStore = usePanelStore();

// Get all filter parameters from the route
const searchFilter = computed(() => {
  const search = route.query.search;
  return typeof search === 'string' ? search : '';
});

const statusFilter = computed(() => {
  const status = route.query.status;
  return typeof status === 'string' ? status : '';
});

const typeFilter = computed(() => {
  const type = route.query['campaign-type'];
  return typeof type === 'string' ? type : '';
});

const coachFilter = computed(() => {
  const coach = route.query.coach;
  return typeof coach === 'string' ? coach : '';
});

// Create a query params object for API requests
const queryParams = computed(() => ({
  search: searchFilter.value,
  status: statusFilter.value,
  type: typeFilter.value,
  coach: coachFilter.value,
}));

// API call for statistics with cursor-based pagination
const {
  data: statisticsPages,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isLoading
} = useInfiniteQuery<StatisticsPage, Error, InfiniteStatisticsData>({
  queryKey: ['campaign-statistics', queryParams],
  queryFn: async ({ pageParam, queryKey }) => {
    // Extract the query parameters from the queryKey
    const [_, params] = queryKey as [string, typeof queryParams.value];
    
    const response = await getCampaignStatistics({ 
      search: params.search,
      status: params.status,
      type: params.type,
      coach: params.coach,
      cursor: pageParam as string | undefined,
    });
    return response.data;
  },
  getNextPageParam: (lastPage: StatisticsPage) => lastPage.hasMore ? lastPage.cursor : undefined,
  initialPageParam: undefined,
});

// Flatten all pages into a single array of campaigns
const allCampaigns = computed<CampaignsStatistics[]>(() => 
  statisticsPages.value?.pages.flatMap(page => page.items as CampaignsStatistics[]) ?? []
);

</script>

<template>
  <div class="campaign-statistics flex-col w-full px-[30px] pt-5">
    <RouterView />
    <h1 class="text-xl font-bold mb-4">{{ $t('campaigns.statistics.campaign-prospects-tunnels') }}</h1>
    
    <div v-if="isLoading && !isFetchingNextPage" class="flex w-full justify-center py-5">
      <BcSpinner size="large" />
    </div>
    
    <div v-else-if="allCampaigns.length === 0" class="text-center py-10 text-gray-500">
      Aucun résultat ne correspond à votre recherche
    </div>
    
    <div v-else class="flex flex-col">
      <div class="statistics-grid mt-5 rounded-lg border border-blue-200 shadow overflow-hidden w-fit">
        <!-- Grid Layout -->
        <div class="grid grid-cols-8 bg-neutral-200 text-xs text-blue-900">
          <!-- Grid Header -->
          <div class="grid-cell grid-header-cell">
            {{ $t('campaigns.statistics.headers.campaign') }}
          </div>
          <div class="grid-cell grid-header-cell text-center">
            {{ $t('campaigns.statistics.headers.prospects-to-send') }}
          </div>
          <div class="grid-cell grid-header-cell text-center">
            {{ $t('campaigns.statistics.headers.prospects-ready-to-send') }}
          </div>
          <div class="grid-cell grid-header-cell text-center">
            {{ $t('campaigns.statistics.headers.prospects-in-progress') }}
          </div>
          <div class="grid-cell grid-header-cell text-center">
            {{ $t('campaigns.statistics.headers.prospects-completed') }}
          </div>
          <div class="grid-cell grid-header-cell text-center">
            {{ $t('campaigns.statistics.headers.contacted') }}
          </div>
          <div class="grid-cell grid-header-cell text-center">
            {{ $t('campaigns.statistics.headers.accepted-invitation') }}
          </div>
          <div class="grid-cell grid-header-cell text-center">
            {{ $t('campaigns.statistics.headers.answered') }}
          </div>
          <!-- Grid Rows -->
          <CampaignStatisticsCard 
            v-for="(campaign, index) in allCampaigns" 
            :key="campaign._id || index" 
            :campaign="campaign" 
            :index="index"
          />
        </div>
      </div>
      
      <!-- Add the infinite scroll trigger at the end -->
      <InfiniteScrollTrigger
        :has-more="hasNextPage"
        :is-loading-more="isFetchingNextPage"
        spinner-size="medium"
        @load-more="fetchNextPage"
        class="justify-center"
      />
    </div>

    <BcModal 
      :active="!!panelStore.modal" 
      @close="panelStore.closeModal()"
      :is-panel="true"
    >
      <component 
        :is="panelStore.modal" 
        v-bind="panelStore.modalProps"
      />
    </BcModal>
  </div>
</template>

<style lang="scss" scoped>
.grid-header-cell {
  @apply py-3 px-6 text-left font-medium;
}

.grid-cell {
  @apply px-4 py-1 h-[3.5rem] flex items-center border-b border-gray-200;
}
</style> 